import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { SHOP } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TID = {
  pill: (brand) => `brand-pill-${brand.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
  pillAll: "brand-pill-all",
  bar: "brand-filter-bar",
};

function BrandLogo({ brand, image }) {
  const [src, setSrc] = useState(image);
  const tried = useRef(false);
  useEffect(() => setSrc(image), [image]);
  const generate = () => {
    if (tried.current) return;
    tried.current = true;
    axios.post(`${API}/brands/${encodeURIComponent(brand)}/auto-image`).then((r) => setSrc(r.data?.image)).catch(() => {});
  };
  useEffect(() => {
    if (!image) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);
  if (!src) {
    return (
      <div className="w-full h-10 grid place-items-center text-lg font-black text-gray-300">
        {brand.slice(0, 1)}
      </div>
    );
  }
  return (
    <img
      src={src.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src}
      alt={brand}
      loading="lazy"
      onError={generate}
      className="w-full h-10 object-contain"
    />
  );
}

export default function BrandFilter({ brands = [], active, onSelect, productCounts = {} }) {
  const [images, setImages] = useState({});
  const scrollerRef = useRef(null);
  const pauseRef = useRef(() => {});

  useEffect(() => {
    axios.get(`${API}/brands`).then((r) => {
      const map = {};
      for (const b of r.data || []) map[b.name] = b.image;
      setImages(map);
    }).catch(() => {});
  }, []);

  // Slow continuous auto-scroll (ping-pong), pausing on hover / touch / manual scroll.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let dir = 1, raf, hovering = false, paused = false, resumeTimer;
    const tempPause = () => {
      paused = true;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { paused = false; }, 3000);
    };
    pauseRef.current = tempPause;
    const onEnter = () => { hovering = true; };
    const onLeave = () => { hovering = false; };
    let pos = el.scrollLeft;
    const step = () => {
      if (!hovering && !paused && el.scrollWidth > el.clientWidth + 4) {
        if (Math.abs(el.scrollLeft - pos) > 2) pos = el.scrollLeft; // resync after manual scroll
        pos += 0.35 * dir;
        const max = el.scrollWidth - el.clientWidth;
        if (pos >= max) { pos = max; dir = -1; }
        else if (pos <= 0) { pos = 0; dir = 1; }
        el.scrollLeft = pos;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("touchstart", tempPause, { passive: true });
    el.addEventListener("wheel", tempPause, { passive: true });
    el.addEventListener("pointerdown", tempPause);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resumeTimer);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("touchstart", tempPause);
      el.removeEventListener("wheel", tempPause);
      el.removeEventListener("pointerdown", tempPause);
    };
  }, [brands.length]);

  if (!brands || brands.length === 0) return null;

  const pick = (b) => {
    onSelect?.(b);
    setTimeout(() => {
      document.querySelector(`[data-testid="${SHOP.productsSection}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };
  const scrollBy = (dx) => {
    pauseRef.current();
    scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  };

  return (
    <div data-testid={TID.bar} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.22em] text-gray-500 font-bold">
          Shop by brand
        </div>
        <div className="hidden sm:flex gap-1.5">
          <button data-testid="brand-scroll-left" onClick={() => scrollBy(-320)} aria-label="Scroll brands left" className="w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-600 font-black hover:border-gray-400">‹</button>
          <button data-testid="brand-scroll-right" onClick={() => scrollBy(320)} aria-label="Scroll brands right" className="w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-600 font-black hover:border-gray-400">›</button>
        </div>
      </div>
      <div ref={scrollerRef} className="flex gap-2.5 overflow-x-auto -mx-1 px-1 pb-2" style={{ scrollbarWidth: "thin" }}>
        <button
          data-testid={TID.pillAll}
          onClick={() => pick(null)}
          className={`shrink-0 w-28 h-[4.5rem] rounded-xl text-xs font-extrabold uppercase tracking-wider border transition-all grid place-items-center ${
            !active
              ? "border-[#0056D2] bg-[#0056D2] text-white shadow-sm"
              : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
          }`}
        >
          All brands
        </button>
        {brands.map((b) => {
          const isActive = active === b;
          const count = productCounts[b] || 0;
          return (
            <button
              key={b}
              data-testid={TID.pill(b)}
              onClick={() => pick(b)}
              className={`shrink-0 w-32 h-[4.5rem] rounded-xl border transition-all px-2 py-1.5 flex flex-col items-center justify-center gap-1 ${
                isActive
                  ? "border-[#0056D2] ring-2 ring-[#0056D2]/30 bg-white shadow-sm"
                  : count === 0
                  ? "border-gray-200 bg-white opacity-50"
                  : "border-gray-300 bg-white hover:border-gray-400 hover:shadow-sm"
              }`}
            >
              <BrandLogo brand={b} image={images[b]} />
              <span className={`text-[10px] font-extrabold uppercase tracking-wider truncate max-w-full ${isActive ? "text-[#0056D2]" : "text-gray-600"}`}>
                {b}{count > 0 ? ` · ${count}` : ""}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
